import React from 'react';
import { Text, View, StyleSheet, Link } from '@react-pdf/renderer';
import { customColors } from '../misc';

const md = 10;
const lg = 14;
const xl = 18;

const styles = StyleSheet.create({
  p: {
    fontFamily: 'eina03regular',
    fontSize: md,
    color: '#1d1d1f',
  },
  h1: {
    fontFamily: 'eina03bold',
    fontSize: xl,
  },
  h2: { fontFamily: 'eina03semibold', fontSize: lg },
  strong: { fontFamily: 'eina03bold' },
  underline: { textDecoration: 'underline' },
  link: {
    fontFamily: 'eina03semibold', // <- important
    color: customColors.BLUE,
    textDecoration: 'underline',
  },
  liRow: { flexDirection: 'row', alignItems: 'flex-start' },
  liBulletCol: { width: 20, alignItems: 'flex-end', fontSize: md },
  liContentCol: { flex: 1, marginLeft: 6, fontSize: md },
  liBulletText: {
    fontFamily: 'eina03semibold',
    fontSize: md,
    color: '#1d1d1f',
  },
  bulletDot: {
    width: 4,
    height: 4,
    borderRadius: 1.5,
    backgroundColor: '#1d1d1f',
    margin: '0 auto',
  },
  list: {
    marginBottom: 15,
  },
  section: {
    marginBottom: 10,
  },
});

const FORMAT = {
  BOLD: 1,
  ITALIC: 2,
  STRIKETHROUGH: 4,
  UNDERLINE: 8,
};

function safeParse(input) {
  if (!input) return null;
  if (typeof input === 'object') return input;
  try {
    return JSON.parse(input);
  } catch {
    return null;
  }
}

function textAlignFrom(nodeFormat) {
  if (nodeFormat === 'center') return 'center';
  if (nodeFormat === 'right') return 'right';
  if (nodeFormat === 'justify') return 'justify';
  return 'left';
}

function normalizeUrl(url) {
  if (!url || typeof url !== 'string') return null;
  if (/^(https?:|mailto:|tel:|#)/.test(url)) return url;
  return `https://${url}`;
}

function inlineTextStyle(bitmask = 0) {
  const arr = [];
  if (bitmask & FORMAT.BOLD) arr.push(styles.strong);
  // Only push italic if style exists (we didn't register italic by default)
  if (bitmask & FORMAT.ITALIC && styles.em) arr.push(styles.em);
  if (bitmask & FORMAT.UNDERLINE) arr.push(styles.underline);
  return arr;
}

function renderInline(children, keyBase) {
  if (!Array.isArray(children)) return null;
  return children.map((c, i) => renderNode(c, `${keyBase}-t-${i}`));
}

function renderList(node, keyBase) {
  const isNumbered = node.listType === 'number' || node.tag === 'ol';
  const start = typeof node.start === 'number' ? node.start : 1;
  const items = node.children || [];
  return (
    <View key={keyBase} style={styles.list}>
      {items.map((li, idx) => {
        const marker = isNumbered ? `${li.value ?? start + idx}.` : '•';
        return (
          <View key={`${keyBase}-li-${idx}`} style={styles.liRow}>
            <Text style={styles.liBulletCol}>{marker}</Text>
            <View style={styles.liContentCol}>
              {renderNode(li, `${keyBase}-lic-${idx}`)}
            </View>
          </View>
        );
      })}
    </View>
  );
}

function renderNode(node, keyBase = 'k') {
  if (!node) return null;

  switch (node.type) {
    case 'root':
      return (node.children || []).map((c, i) =>
        renderNode(c, `${keyBase}-r-${i}`),
      );

    case 'paragraph': {
      const align = textAlignFrom(node.format);
      return (
        <Text key={keyBase} style={[styles.p, { textAlign: align }]}>
          {renderInline(node.children, keyBase)}
        </Text>
      );
    }

    case 'heading': {
      const align = textAlignFrom(node.format);
      const style =
        node.tag === 'h1'
          ? styles.h1
          : node.tag === 'h2'
          ? styles.h2
          : styles.h2;
      return (
        <Text key={keyBase} style={[style, { textAlign: align }]}>
          {renderInline(node.children, keyBase)}
        </Text>
      );
    }

    case 'text': {
      const style = inlineTextStyle(node.format);
      return (
        <Text key={keyBase} style={style}>
          {node.text || ''}
        </Text>
      );
    }

    case 'linebreak':
      return '\n';

    case 'link': {
      const url = normalizeUrl(node.url);
      const children = renderInline(node.children, keyBase);

      if (!url) {
        return (
          <Text key={keyBase} style={styles.link}>
            {children}
          </Text>
        );
      }

      return (
        <Link key={keyBase} src={url} style={styles.link}>
          {children}
        </Link>
      );
    }

    case 'list':
      return renderList(node, keyBase);

    case 'listitem':
      if (Array.isArray(node.children)) {
        return node.children.map((c, i) =>
          renderNode(c, `${keyBase}-lii-${i}`),
        );
      }
      return null;

    default:
      return Array.isArray(node.children)
        ? node.children.map((c, i) => renderNode(c, `${keyBase}-u-${i}`))
        : null;
  }
}

export function LexicalPdf({ content }) {
  const json = safeParse(content);
  const root = json?.root || json;
  if (!root) return null;
  return <View style={styles.section}>{renderNode(root, 'root')}</View>;
}
